import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { ShareBar } from '@/components/ShareBar';
import { DocEditor } from '@/components/DocEditor';
import { AppLayout } from '@/components/layout/AppLayout';

const EXAMPLES = [
  { label: "Non-Disclosure Agreement", color: "bg-secondary-container text-on-secondary-container border-secondary" },
  { label: "Executive Summary", color: "bg-tertiary-container text-on-tertiary-container border-tertiary" },
  { label: "Meeting Minutes", color: "bg-surface-container text-on-surface-variant border-outline-variant" },
  { label: "French Revolution Summary", color: "bg-secondary-container text-on-secondary-container border-secondary" },
  { label: "Academic CV", color: "bg-tertiary-container text-on-tertiary-container border-tertiary" }
];

export function HomePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [html, setHtml] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [slug, setSlug] = useState<string | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const state = location.state as { snippet?: string; prompt?: string; title?: string } | null;
    if (state) {
      if (state.prompt) setPrompt(state.prompt);
      if (state.snippet) {
        setHtml(`<!DOCTYPE html><html><head><style>body{font-family:'Source Serif 4',serif;padding:4rem;color:#241916;background:#ffffff;}h1{font-family:'Playfair Display',serif;font-size:3rem;margin-bottom:2rem;}</style></head><body><h1>${state.title || 'Draft'}</h1><p>${state.snippet}</p></body></html>`);
      }
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  const handleGenerate = async () => {
    if (!prompt.trim()) return toast.error('Describe your vision first.');
    setIsGenerating(true);
    setSlug(null);
    try {
      const res = await fetch('/api/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt })
      });
      const data = await res.json() as any;
      if (data.success) {
        setHtml(data.data);
        toast.success('Manuscript forged.');
      } else {
        toast.error(data.error || 'The forge cooled unexpectedly.');
      }
    } catch (e) {
      toast.error('Connection to the forge lost.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async (status: 'draft' | 'archived' = 'draft') => {
    if (!html) return;
    setIsSaving(true);
    const titleMatch = html.match(/<h1[^>]*>(.*?)<\/h1>/i) || html.match(/<title[^>]*>(.*?)<\/title>/i);
    const title = titleMatch 
      ? titleMatch[1].replace(/<[^>]*>?/gm, '').trim() 
      : 'Untitled Archive';
    try {
      const res = await fetch('/api/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ html, title, status })
      });
      const data = await res.json() as any;
      if (data.success) {
        setSlug(data.data);
        toast.success(status === 'archived' ? 'Finalized in archives.' : 'Draft preserved in library.');
      }
    } catch (e) {
      toast.error('Failed to preserve manuscript.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleGenerate();
    }
  };

  return (
    <AppLayout>
      <div className="flex-1 flex flex-col h-full overflow-hidden bg-surface-container-low">
        {/* Prompt Panel */}
        <section className="bg-surface-container-highest border-b border-outline-variant p-gutter shadow-sm relative z-10 shrink-0">
          <div className="max-w-max-width-doc mx-auto w-full">
            <label className="block font-label-caps text-label-caps text-on-surface-variant mb-2 tracking-widest uppercase" htmlFor="prompt-input">
              Describe Your Document
            </label>
            <div className="relative bg-surface rounded-lg border border-outline-variant shadow-sm focus-within:border-primary focus-within:ring-1 focus-within:ring-primary transition-all overflow-hidden mb-4">
              <textarea 
                className="w-full bg-transparent border-none outline-none resize-y p-4 font-body-lg text-body-lg text-on-surface placeholder-on-surface-variant focus:ring-0" 
                id="prompt-input" 
                placeholder="Instruct the AI scribe. E.g., 'Draft a formal letter to the board of directors outlining Q3 strategy...'" 
                rows={3}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <div className="bg-surface-container-low px-4 py-3 border-t border-outline-variant flex justify-between items-center">
                <span className="font-label-caps text-[10px] text-on-surface-variant uppercase tracking-wider">
                  Ctrl + Enter to generate · Scriptorium Engine V1.2
                </span>
                <button 
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className="bg-primary-container text-on-primary-container px-6 py-2 rounded-DEFAULT font-label-caps text-label-caps flex items-center gap-2 hover:bg-surface-tint active:translate-y-px active:translate-x-px transition-all shadow-sm disabled:opacity-50"
                >
                  {isGenerating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <span className="material-symbols-outlined text-[18px]">auto_awesome</span>
                  )}
                  {isGenerating ? 'Forging...' : 'Generate'}
                </button>
              </div>
            </div>
            
            {/* Examples Chips */}
            <div className="flex flex-wrap gap-2 items-center">
              <span className="font-label-caps text-[10px] text-on-surface-variant mr-2 uppercase tracking-widest">Examples:</span>
              {EXAMPLES.map(ex => (
                <button 
                  key={ex.label}
                  onClick={() => setPrompt(ex.label)}
                  className={`shrink-0 px-4 py-2 rounded-full font-label-caps text-[11px] border shadow-sm transition-all uppercase tracking-wider ${ex.color} hover:brightness-110 active:scale-95`}
                >
                  {ex.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Document Preview Canvas (The "Desk") */}
        <section className="flex-1 bg-surface-dim p-4 md:p-margin-page flex flex-col items-center overflow-y-auto relative scroll-smooth">
          <div className="w-full max-w-max-width-doc flex flex-col gap-8">
            {html && (
               <div className="flex items-center justify-between bg-inverse-surface text-inverse-on-surface p-4 rounded-xl shadow-lg animate-in slide-in-from-top-4 duration-500 mb-4">
                 <div className="flex items-center gap-2">
                   <span className="material-symbols-outlined text-secondary">ink_pen</span>
                   <span className="font-label-caps text-[11px] font-bold uppercase tracking-widest">Active Manuscript</span>
                 </div>
                 <ShareBar html={html} title="Document" saving={isSaving} savedSlug={slug} onSave={handleSave} />
               </div>
            )}

            <div className="w-full bg-surface min-h-[800px] shadow-md border border-outline-variant relative flex flex-col ink-drop-shadow transition-all duration-500 hover:shadow-lg">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-b from-surface-variant to-transparent opacity-30 pointer-events-none"></div>
              <div className="absolute bottom-0 left-0 w-full h-2 bg-gradient-to-t from-surface-variant to-transparent opacity-30 pointer-events-none"></div>
              
              <div className="flex-1 w-full relative">
                {isGenerating ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-surface/80 z-20 backdrop-blur-[2px]">
                    <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
                    <p className="font-h2 text-h2 text-primary animate-pulse italic">Breathing life into the parchment...</p>
                  </div>
                ) : html ? (
                  <DocEditor html={html} onChange={setHtml} />
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center opacity-40 p-20 text-center">
                    <span className="material-symbols-outlined text-[64px] text-on-surface-variant mb-4 font-light">description</span>
                    <h2 className="font-h2 text-h2 text-on-surface-variant mb-2">The Empty Canvas</h2>
                    <p className="font-body-md text-body-md text-on-surface-variant max-w-md">
                      Awaiting the inscription of your intent. Utilize the prompt interface above to summon the AI scribe.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="text-center pb-12">
              <p className="font-label-caps text-[10px] text-on-surface-variant tracking-widest uppercase opacity-80">
                Hover any section · Edit to type · Improve with AI · Reset to original
              </p>
            </div>
          </div>
        </section>
      </div>
    </AppLayout>
  );
}